// The filters: whose, category and whether done tasks show. One set for the whole app, remembered on
// the device; the Plan tab, every Calendar mode and search all follow it.

import type { CalendarOwner, Task } from '@/types'

/** Nothing chosen shows everything. Shared: shared tasks for me or either of us. Mine and the partner's leave out "either of us". */
export type WhoseFilter = 'shared' | 'mine' | 'partner' | null

export interface TaskFilter {
  whose: WhoseFilter
  categoryId: string | null
  /** Under Shared, my private tasks show too: the lock beside the Whose button, open. Absent on filters saved before it existed. */
  sharedWithPrivate?: boolean
  /** Done tasks leave the Plan and the Calendar: the struck lines on days, and Done earlier this week. Absent on filters saved before it existed. */
  hideDone?: boolean
}

export const EMPTY_FILTER: TaskFilter = { whose: null, categoryId: null, sharedWithPrivate: false, hideDone: false }

/**
 * Whose, as the top bar's picker offers it: All, Mine, the other person's, Shared.
 * The other person's is left out until they have joined.
 */
export function whoseChoices(otherName: string | null): { value: WhoseFilter; label: string }[] {
  const choices: { value: WhoseFilter; label: string }[] = [
    { value: null, label: 'All' },
    { value: 'mine', label: 'Mine' },
  ]
  if (otherName) choices.push({ value: 'partner', label: `${otherName}'s` })
  choices.push({ value: 'shared', label: 'Shared' })
  return choices
}

/** The name of a Whose choice, such as "Mine" or "Begüm's"; nothing chosen is "All". */
export function whoseLabel(whose: WhoseFilter, otherName: string | null): string {
  return whoseChoices(otherName).find((c) => c.value === whose)?.label ?? 'All'
}

export function activeFilterCount(f: TaskFilter): number {
  return (f.whose ? 1 : 0) + (f.categoryId ? 1 : 0) + (f.hideDone ? 1 : 0)
}

/**
 * Mine shows my private tasks and shared tasks assigned to me. The partner's
 * shows those assigned to them. Shared shows the shared tasks I have a hand in:
 * mine and either of us, never the partner's alone, which only All shows with mine;
 * with its lock open it shows my private tasks as well. The partner's private tasks
 * never reach this device, so a private task here is always mine.
 */
export function matchesWhose(t: Pick<Task, 'scope' | 'assigneeUid'>, whose: WhoseFilter, meUid: string, sharedWithPrivate = false): boolean {
  if (!whose) return true
  if (t.scope === 'private') return whose === 'mine' || (whose === 'shared' && sharedWithPrivate)
  const assignee = t.assigneeUid ?? null
  if (whose === 'shared') return assignee === null || assignee === meUid
  if (assignee === null) return false
  return whose === 'mine' ? assignee === meUid : assignee !== meUid
}

export function matchesFilter(t: Task, f: TaskFilter, meUid: string): boolean {
  if (!matchesWhose(t, f.whose, meUid, f.sharedWithPrivate)) return false
  if (f.categoryId && t.categoryId !== f.categoryId) return false
  return true
}

/**
 * Events follow Whose through whose calendar they are on; a calendar marked
 * Both counts as shared and as each person's. Category never hides events.
 */
export function matchesEventWhose(owner: CalendarOwner, whose: WhoseFilter): boolean {
  if (!whose) return true
  if (whose === 'shared') return owner === 'both'
  if (whose === 'mine') return owner !== 'other'
  return owner !== 'me'
}

/**
 * The filter that shows a task the current one hides: the same filter with
 * whatever excludes it switched to match it. Used to bring a new task into view.
 * A private task added under Shared opens the lock rather than leaving Shared.
 */
export function filterShowing(t: Task, f: TaskFilter, meUid: string): TaskFilter {
  const next = { ...f }
  if (f.whose === 'shared' && t.scope === 'private') next.sharedWithPrivate = true
  else if (!matchesWhose(t, f.whose, meUid, f.sharedWithPrivate)) next.whose = null
  if (f.categoryId && t.categoryId !== f.categoryId) next.categoryId = t.categoryId
  return next
}
